import _ from 'lodash';
import { createSelector } from 'reselect';

import { getItemsListOrdered } from '../../../redux/list/list.utils';
import { StoreState } from '../../../redux/reducers/store.types';
import { DetailsPanelStateType } from '../replication-details-panel/common/parent-child-details.types';
import {
  Replication,
  ReplicationBodyFilters,
  ReplicationsListFilter,
} from '../replications.types';
import { getReplicationDestination } from '../replications.utils';

import { ReplicationsUiState } from './replications-ui.reducer';
import { ReplicationsState } from './replications.reducer';

export const getReplications = (state: StoreState): ReplicationsState =>
  state.replications;

export const getReplicationsList = createSelector(
  getReplications,
  (state: ReplicationsState): Replication[] =>
    getItemsListOrdered(state.replicationsList),
);

export const getReplicationsUi = createSelector(
  getReplications,
  (state: ReplicationsState): ReplicationsUiState => state.replicationsUi,
);

export const getReplicationsListFiltered = createSelector(
  getReplicationsList,
  getReplicationsUi,
  (list: Replication[], ui: ReplicationsUiState): Replication[] => {
    if (ui.replicationsListFilter === ReplicationsListFilter.All) {
      return list;
    }

    return _.filter<Replication>(
      list,
      (replication: Replication) =>
        (replication.platform as string) === ui.replicationsListFilter,
    );
  },
);

export const getReplicationById = createSelector(
  getReplicationsList,
  (list: Replication[]) =>
    _.memoize((replicationId: string): Replication | undefined => {
      return _.find(list, {
        id: replicationId,
      });
    }),
);

export const getSelectedReplicationId = createSelector(
  getReplicationsUi,
  (ui: ReplicationsUiState): string | undefined => ui.selectedReplicationId,
);

export const getSelectedReplication = createSelector(
  getReplicationsListFiltered,
  getSelectedReplicationId,
  (
    visibleReplications: Replication[],
    replicationId: string | undefined,
  ): Replication | undefined => {
    return replicationId === undefined
      ? undefined
      : _.find<Replication>(visibleReplications, { id: replicationId });
  },
);

export const isReplicationVersionDetailsOpened = createSelector(
  getReplicationsUi,
  (ui: ReplicationsUiState): boolean =>
    ui.isAddingNew || ui.isVersionDetailsOpened,
);

export const getEditingReplicationId = createSelector(
  getReplicationsUi,
  (ui: ReplicationsUiState): string | undefined => ui.editableReplicationId,
);

export const isEditingReplicationChangingStatus = createSelector(
  getReplicationsUi,
  (ui: ReplicationsUiState): boolean =>
    ui.editingReplicationChangingActiveStatus,
);

const getEditingReplication = createSelector(
  getReplicationsList,
  getEditingReplicationId,
  (
    list: Replication[],
    editingReplicationId: string | undefined,
  ): Replication | undefined => {
    if (!editingReplicationId) {
      throw new Error('No editing replication id in replications list');
    }

    const replication = _.find(list, {
      id: editingReplicationId,
    });

    if (!replication) {
      throw new Error('No editing replication in replications list');
    }

    return replication;
  },
);

export const getEditingReplicationActiveStatus = createSelector(
  getEditingReplication,
  (replication: Replication) => replication.enabled,
);

export const getEditingReplicationBucketName = createSelector(
  getEditingReplication,
  (replication: Replication) => replication.replica.bucket,
);

export const getEditingReplicationDestination = createSelector(
  getEditingReplication,
  (replication: Replication) =>
    getReplicationDestination(replication.replica.platform),
);

export const getEditingReplicationName = createSelector(
  getEditingReplication,
  (replication: Replication) => replication.name,
);

export const getEditingReplicationBodyFilters = createSelector(
  getEditingReplication,
  (replication: Replication): ReplicationBodyFilters[] => {
    if (replication.copy) {
      return replication.copy.on.bodyFilters;
    }

    return [];
  },
);

export const getViewState = createSelector(
  getReplicationsUi,
  getSelectedReplication,
  (ui: ReplicationsUiState): DetailsPanelStateType => {
    if (ui.isAddingNew) {
      return DetailsPanelStateType.New;
    }

    if (ui.editableReplicationId) {
      return DetailsPanelStateType.Edit;
    }

    return DetailsPanelStateType.None;
  },
);
